import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Network, MapPin, Target, CornerDownRight, ShieldCheck, GitBranch, UserCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, EmptyState } from "../../components/common";
import { Badge } from "../../components/ui/badge";
import { StaffAvatar, StaffLabel } from "../../components/StaffAvatar";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { api, errMsg, fmtINR } from "../../lib/api";
import { TerritoryDialog } from "../../components/TerritoryDialog";
import { SupervisorTargetDialog, TARGET_STATUS_META } from "../../components/SupervisorTargetDialog";
import { CollectorTargetDialog } from "../../components/CollectorTargetDialog";

const ROLE_CLS = {
  municipal_admin: "bg-primary/10 text-primary border-primary/20",
  supervisor: "bg-sky-100 text-sky-800 border-sky-200",
  tax_collector: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

function Chips({ icon: Icon, items, empty }) {
  if (!items || items.length === 0) return <span className="text-[11px] text-amber-600">{empty}</span>;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {items.slice(0, 4).map((z) => (
        <Badge key={z} variant="outline" className="text-[10px] gap-0.5"><Icon className="h-2.5 w-2.5" />{z}</Badge>
      ))}
      {items.length > 4 && <span className="text-[10px] text-muted-foreground">+{items.length - 4} more</span>}
    </span>
  );
}

function TargetStat({ icon: Icon, label, target, collected, pct, status, testId }) {
  const meta = TARGET_STATUS_META[status] || TARGET_STATUS_META.no_target;
  return (
    <div className="rounded-md border bg-muted/30 px-2 py-1.5 min-w-[150px]" data-testid={testId}>
      <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</p>
      <p className="text-xs font-semibold tabular-nums">
        {fmtINR(collected)} <span className="font-normal text-muted-foreground">/ {target != null ? fmtINR(target) : "—"}</span>
      </p>
      <p className="text-[10px] flex items-center gap-1">
        <span className={`rounded px-1 border ${meta.cls}`}>{meta.label}</span>
        {pct != null && <span className="tabular-nums text-muted-foreground">{pct}%</span>}
      </p>
    </div>
  );
}

function PersonCard({ node, level, onTerritory, onTargets, onCollectorTarget }) {
  const { code } = useParams();
  const isCollector = node.role === "tax_collector";
  const isSupervisor = node.role === "supervisor";
  const t = node.targets;
  return (
    <div className={`rounded-lg border bg-card px-3 py-2.5 ${!node.is_active ? "opacity-60" : ""}`}
      data-testid={`org-node-${node.email}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex items-center gap-2.5">
          <StaffAvatar code={code} userId={node.id} name={node.name} photoUrl={node.photo_url} size={level === 0 ? "md" : "sm"} testId={`org-avatar-${node.email}`} />
          <div className="min-w-0">
          <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
            <StaffLabel code={code} userId={node.id} name={node.name} empCode={node.employee_id} />
            <Badge variant="outline" className={`text-[10px] capitalize ${ROLE_CLS[node.role] || ""}`}>{node.role.replace(/_/g, " ")}</Badge>
            {node.can_approve && node.role !== "municipal_admin" && (
              <Badge variant="outline" className="text-[10px] gap-0.5"><ShieldCheck className="h-2.5 w-2.5" /> Approver</Badge>
            )}
            {!node.is_active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
          </div>
          <p className="text-[11px] text-muted-foreground truncate">{node.email}{node.designation ? ` • ${node.designation}` : ""}</p>
          <div className="mt-1">
            {isSupervisor && <Chips icon={MapPin} items={node.zone_names} empty="No zones assigned" />}
            {isCollector && <Chips icon={MapPin} items={node.ward_names} empty="No wards assigned" />}
            {isCollector && node.mohalla_names?.length > 0 && (
              <span className="ml-1 text-[10px] text-muted-foreground">({node.mohalla_names.length} mohalla{node.mohalla_names.length === 1 ? "" : "s"})</span>
            )}
          </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isCollector && (
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground flex items-center gap-1 justify-end"><Target className="h-3 w-3" /> {node.target != null ? fmtINR(node.target) : "No target"}</p>
              <p className="text-xs font-semibold tabular-nums">{fmtINR(node.collected_mtd)} <span className="font-normal text-[10px] text-muted-foreground">MTD</span></p>
            </div>
          )}
          {isSupervisor && onTargets && (
            <Button variant="outline" size="sm" className="h-8" title="Set supervisor targets"
              onClick={() => onTargets(node)} data-testid={`org-set-targets-${node.email}`}>
              <Target className="h-3.5 w-3.5 mr-1.5" /> Set targets
            </Button>
          )}
          {isCollector && onCollectorTarget && (
            <Button variant="outline" size="sm" className="h-8" title="Set this collector's monthly target"
              onClick={() => onCollectorTarget(node)} data-testid={`org-set-collector-target-${node.email}`}>
              <Target className="h-3.5 w-3.5 mr-1.5" /> {node.target != null ? "Edit target" : "Set target"}
            </Button>
          )}
          {(isSupervisor || isCollector) && (
            <Button variant="ghost" size="icon" className="h-8 w-8" title="Assign territory"
              onClick={() => onTerritory(node)} data-testid={`org-territory-${node.email}`}>
              <MapPin className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      {isSupervisor && t && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`org-supervisor-targets-${node.email}`}>
          <TargetStat icon={UserCheck} label="Personal target" target={t.personal_target} collected={t.personal_collected}
            pct={t.personal_achievement_pct} status={t.personal_status} testId={`org-personal-target-${node.email}`} />
          <TargetStat icon={Users} label="Team goal (Admin)" target={t.team_goal} collected={t.team_collected}
            pct={t.team_goal_achievement_pct} status={t.team_goal_status} testId={`org-team-goal-${node.email}`} />
          <TargetStat icon={Target} label="Team target (collectors)" target={t.team_target || null} collected={t.team_collected}
            pct={t.team_target_achievement_pct} status={t.team_target ? (t.team_target_achievement_pct >= 100 ? "achieved" : "on_track") : "no_target"}
            testId={`org-team-target-${node.email}`} />
        </div>
      )}
      {node.sub_users?.length > 0 && (
        <div className="mt-2 space-y-1">
          {node.sub_users.map((s) => (
            <p key={s.id} className="text-[11px] text-muted-foreground flex items-center gap-1 pl-2">
              <CornerDownRight className="h-3 w-3" /> <StaffLabel code={code} userId={s.id} name={s.name} empCode={s.employee_id} /> (sub-user){s.role === "tax_collector" && ` • ${fmtINR(s.collected_mtd)} MTD`}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrgChart() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [territoryFor, setTerritoryFor] = useState(null);
  const [targetsFor, setTargetsFor] = useState(null);
  const [collectorTargetFor, setCollectorTargetFor] = useState(null);

  const load = useCallback(() => api.get(`/t/${code}/org-chart`).then((r) => setData(r.data)), [code]);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  if (!data) return <Spinner />;

  return (
    <div data-testid="org-chart-page">
      <PageHeader title="Org Chart"
        subtitle={`Reporting hierarchy • ${data.counts.admins} admin${data.counts.admins === 1 ? "" : "s"} • ${data.counts.supervisors} supervisor${data.counts.supervisors === 1 ? "" : "s"} • ${data.counts.collectors} collector${data.counts.collectors === 1 ? "" : "s"} • targets & collections for ${data.month}`} />

      <div className="space-y-4">
        {data.admins.map((a) => (
          <Card key={a.id} className="shadow-sm">
            <CardContent className="p-4 space-y-3">
              <PersonCard node={a} level={0} onTerritory={setTerritoryFor} />
              {a.supervisors.length === 0 ? (
                <p className="text-xs text-muted-foreground pl-6">No supervisors mapped to this admin yet.</p>
              ) : (
                <div className="pl-4 md:pl-6 border-l-2 border-border ml-2 space-y-3">
                  {a.supervisors.map((s) => (
                    <div key={s.id} className="space-y-2">
                      <PersonCard node={s} level={1} onTerritory={setTerritoryFor} onTargets={setTargetsFor} />
                      {s.collectors.length === 0 ? (
                        <p className="text-xs text-muted-foreground pl-6">No tax collectors mapped to this supervisor.</p>
                      ) : (
                        <div className="pl-4 md:pl-6 border-l-2 border-border ml-2 space-y-2">
                          {s.collectors.map((c) => <PersonCard key={c.id} node={c} level={2} onTerritory={setTerritoryFor} onCollectorTarget={setCollectorTargetFor} />)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {(data.unmapped_supervisors.length > 0 || data.unmapped_collectors.length > 0) && (
          <Card className="shadow-sm border-amber-200">
            <CardContent className="p-4 space-y-2" data-testid="org-unmapped-section">
              <p className="text-sm font-medium flex items-center gap-2 text-amber-700">
                <GitBranch className="h-4 w-4" /> Not mapped yet
              </p>
              <p className="text-xs text-muted-foreground">
                Map these users from Users &amp; Staff (Change mapping action) so they appear in the hierarchy
                and follow supervisor scoping.
              </p>
              <div className="space-y-2">
                {data.unmapped_supervisors.map((s) => <PersonCard key={s.id} node={s} level={0} onTerritory={setTerritoryFor} onTargets={setTargetsFor} />)}
                {data.unmapped_collectors.map((c) => <PersonCard key={c.id} node={c} level={0} onTerritory={setTerritoryFor} onCollectorTarget={setCollectorTargetFor} />)}
              </div>
            </CardContent>
          </Card>
        )}

        {data.admins.length === 0 && <EmptyState title="No staff users yet" />}
      </div>

      <TerritoryDialog code={code} user={territoryFor} open={!!territoryFor}
        onOpenChange={(v) => !v && setTerritoryFor(null)} onSaved={load} />
      <SupervisorTargetDialog code={code} supervisor={targetsFor} month={data.month}
        onOpenChange={(v) => !v && setTargetsFor(null)} onSaved={load} />
      <CollectorTargetDialog code={code} collector={collectorTargetFor} month={data.month}
        onOpenChange={(v) => !v && setCollectorTargetFor(null)} onSaved={load} />
    </div>
  );
}
